import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ResourceCategorySection from './components/ResourceCategorySection';
import { resourceSections } from '../../mocks/localResources';
import KeepExploring from '../../components/feature/KeepExploring';
import SEO from '../../components/feature/SEO';

const categoryIcons: Record<string, string> = {
  food: 'ri-restaurant-2-line',
  family: 'ri-parent-line',
  health: 'ri-heart-pulse-line',
  'mental-health': 'ri-mental-health-line',
  housing: 'ri-home-heart-line',
  community: 'ri-book-open-line',
};

export default function LocalResourcesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Local Family Resources in Gwinnett County | The Mommy Mingle',
    description:
      'Curated local resources for moms and families in Gwinnett County including food assistance, health services, mental health support, and community programs.',
    url: `${siteUrl}/local-resources`,
    publisher: {
      '@type': 'Organization',
      name: 'The Mommy Mingle',
      url: siteUrl,
    },
    about: [
      { '@type': 'Thing', name: 'Food Assistance' },
      { '@type': 'Thing', name: 'Family Support Services' },
      { '@type': 'Thing', name: 'Mental Health Resources' },
      { '@type': 'Thing', name: 'Housing Support' },
      { '@type': 'Thing', name: 'Community Programs' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Local Family Resources in Gwinnett County | The Mommy Mingle"
        description="Access curated local resources for moms and families in Gwinnett County including food assistance, health services, mental health support, and community programs."
        keywords="local resources Gwinnett County, family support Georgia, food assistance Gwinnett, mental health resources moms"
        canonical="/local-resources"
        schema={schema}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 px-4 bg-gradient-to-b from-rose-50 to-white border-b border-rose-100">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-widest text-rose-400 uppercase mb-4">
            Gwinnett County &amp; Surrounding Areas
          </p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
            Local Help
          </h1>
          <p className="text-lg text-gray-500 leading-relaxed max-w-lg mx-auto">
            Real help, right here when you need it.
          </p>
        </div>

        {/* Quick-jump category nav */}
        <div className="max-w-3xl mx-auto mt-10 px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {resourceSections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border ${section.borderColor} ${section.color} ${section.bgColor} hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap`}
              >
                <i className={`${categoryIcons[section.id] ?? section.icon} text-xs`}></i>
                {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Resource Sections */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-20">
        {resourceSections.map((section, index) => (
          <div key={section.id}>
            {index > 0 && <div className="border-t border-gray-100 mb-20" />}
            <ResourceCategorySection section={section} />
          </div>
        ))}
      </main>

      {/* Bottom Callout */}
      <section className="bg-rose-50 border-t border-rose-100 py-16">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-5">
            <i className="ri-add-circle-line text-rose-500 text-xl"></i>
          </div>
          <p className="text-base font-semibold text-gray-800 mb-2">Know a resource we're missing?</p>
          <p className="text-sm text-gray-500 leading-relaxed mb-6">
            Message us so we can keep this list updated for local moms.
          </p>
          <a
            href="mailto:hello@themommymingle.com"
            className="inline-flex items-center gap-2 bg-white border border-rose-200 text-rose-600 text-sm font-semibold px-6 py-3 rounded-full hover:bg-rose-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line"></i>
            Send Us a Message
          </a>
        </div>
      </section>

      <KeepExploring
        title="Keep Exploring"
        subtitle="You're not alone — local moms across Gwinnett and surrounding areas are here."
        items={[
          { label: 'Explore Things To Do', icon: 'ri-compass-3-line', to: '/things-to-do' },
          { label: 'See Local Events', icon: 'ri-calendar-event-line', to: '/events' },
          { label: 'Join The Mommy Mingle', icon: 'ri-group-line', href: 'https://www.facebook.com/groups/themommymingle', primary: true },
        ]}
        bg="white"
      />

      <Footer />
    </div>
  );
}
